import React, { createContext, useEffect, useState } from "react";

import { apiFetch, apiOrderCheckoutFetch } from "../apiBindings";
import { navigate } from "../components/shared/LinkComponent";
import usePrevious from "../hooks/usePrevious";
import pushToAnalytics from "../utils/analytics";
import {
    storageLocal as storage,
    storageSession as session,
} from "../utils/StorageHandler";
import checkoutSteps from "./GasCheckoutSteps";
import { fetchProducts } from "./GasDataProvider";
import gasPaymentTypes from "./GasPaymentTypes";

export const DataContext = createContext();

const GasCheckoutDataProvider = ({ checkoutStep, children }) => {
    const baseUrl = "/gas/bestellung/";
    const steps = 3;
    const [loading, setLoading] = useState(false);
    const [currentStepIndex, setCurrentStepIndex] = useState(0);
    const [editMode, setEditMode] = useState(false);

    const [detailsChanged, setDetailsChanged] = useState(0);
    const [paymentTypeChanged, setPaymentTypeChanged] = useState(0);
    const [stepButtonAction, setStepButtonAction] = useState(null);
    const [overlayContent, setOverlayContent] = useState(null);
    const [checkoutData, setCheckoutData] = useState(
        session.getJson("gas:checkout")
    );

    const [orderData, setOrderData] = useState(
        session.getJson("gas:order"),
        null
    );
    const [deliveryEqualBilling, setDeliveryEqualBilling] = useState(
        storage.getInt("gas:deliveryequalbilling") !== null
            ? storage.getInt("gas:deliveryequalbilling")
            : 0
    );

    const [street] = useState(storage.getJson("gas:street"));
    const [houseNum] = useState(storage.get("gas:housenumber"));
    const [place] = useState(storage.getJson("gas:place"));
    const [zipcode] = useState(storage.get("gas:zipcode"));

    const [consumption, setConsumption] = useState(
        storage.get("gas:consumption")
    );
    const previousStepIndex = usePrevious(currentStepIndex);

    /**
     * Name of the product
     */
    const [chosenTarifName, setChosenTarifName] = useState(
        storage.get("gas:tarif-name")
    );

    const [verbandsnummer] = useState(
        storage.get("gas:verbandsnummer") !== null
            ? storage.get("gas:verbandsnummer")
            : ""
    );

    /**
     * Result with prices
     */
    const [result, setResult] = useState(
        session.getJson("gas:result") !== null
            ? session.getJson("gas:result")
            : null
    );

    /**
     * Data for the delivery address step.
     */
    const [deliveryAddressData, setDeliveryAddressData] = useState(
        session.getJson("gas:delivery-address", {
            company: "",
            salutation: "Herr",
            birthdate: "",
            firstname: "",
            lastname: "",
            email: "",
            phone: "",
        })
    );
    /**
     * Data for the billing address step.
     */
    const [billingAddressData, setBillingAddressData] = useState(
        storage.getJson("gas:billing-address", {
            billingsalutation: "Herr",
            billingfirstname: "",
            billinglastname: "",
            billingemail: "",
            billingphone: "",
            billingstreet: "",
            billingzipcode: "",
            billingcity: "",
        })
    );

    const [paymentData, setPaymentData] = useState(
        storage.getJson("gas:payment-data", {
            payment_payment_type: 1,
            payment_account_holder: "",
            payment_bank_name: "",
            payment_sepa_direct_granted: false,
            payment_different_account_holder: false,
            payment_salutation: "Herr",
            payment_firstname: "",
            payment_lastname: "",
            payment_email: "",
            payment_phone: "",
            payment_street: "",
            payment_zipcode: "",
            payment_city: "",
        })
    );
    const [iban, setIban] = useState(
        storage.get("gas:iban") !== null ? storage.get("gas:iban") : ""
    );
    const [deliveryDetails, setDeliveryDetails] = useState(
        session.getJson("gas:delivery-details", {
            delivery_movement_type: "Umzug/Einzug",
            delivery_counter_number: "",
            delivery_change_date: "",
            delivery_previous_supplier: "",
            delivery_change_date_immediately: "nächstmöglich",
            delivery_supplier_terminated: "Nein",
            delivery_supplier_terminated_date: new Date(
                new Date().setDate(new Date().getDate() + 14)
            )
                .toISOString()
                .slice(0, 10),
            aktionscode: "",
        })
    );

    const [orderComment, setOrderComment] = useState(
        storage.get("gas:order-comment", "")
    );

    /**
     * Chosen product
     */

    const [productChoice, setProductChoice] = useState(
        storage.get("gas:product-choice") !== null
            ? storage.get("gas:product-choice")
            : 0
    );

    /**
     * AGB checked
     */
    const [agbChecked, setAgbChecked] = useState(false);

    /**
     * The current price we have.
     */
    const [price, setPrice] = useState({
        monthly_charge: 0,
        unit_price: 0,
        price_total_gross: 0,
    });

    /**
     * Form validation errors.
     */
    const [formErrors, setFormErrors] = useState([]);

    /**
     *  Receive Newsletter
     */
    const [receiveNewsletter, setReceiveNewsletter] = useState(
        storage.getInt("gas:receive-newsletter")
            ? storage.getInt("gas:receive-newsletter")
            : 0
    );

    /**
     * When the user changes the payment type in the overlay.
     * Will be saved without pushing the save button.
     */
    useEffect(() => {
        if (overlayContent === "zahlart") {
            storage.set(
                "gas:gtag:current-value",
                paymentData.payment_payment_type
            );
        }
        if (paymentData.payment_payment_type == 2) {
            setFormErrors([]);
        }
    }, [paymentData.payment_payment_type, overlayContent]);

    /**
     * When the user changes deliveryequalbilling.
     */
    useEffect(() => {
        storage.set("gas:deliveryequalbilling", deliveryEqualBilling);
        if (deliveryEqualBilling === 0 && formErrors) {
            setFormErrors([]);
        }
    }, [deliveryEqualBilling]);

    /**
     * Sync result with local storage.
     */
    useEffect(() => {
        session.setJson("gas:result", result);
    }, [result]);

    /**
     * When the user accepts the agb.
     */
    useEffect(() => {
        if (agbChecked) {
            clearFormError("agb");
        }
    }, [agbChecked]);

    /**
     * When the user change acceptance of Newsletter
     */
    useEffect(() => {
        storage.set("gas:receive-newsletter", receiveNewsletter);
    }, [receiveNewsletter]);
    /**
     * When the user changes the product in the overlay.
     * Will be saved without pushing the save button.
     */
    useEffect(() => {
        if (overlayContent === "produkt") {
            storage.set("gas:gtag:current-value", productChoice);
        }
    }, [productChoice, overlayContent]);
    /**
     * When the user changes the consumption in the overlay.
     * Will be saved without pushing the save button.
     */
    useEffect(() => {
        if (overlayContent === "consumption") {
            storage.set("gas:gtag:current-value", consumption);
        }
    }, [consumption, overlayContent]);

    /**
     * When the user changes the comment in the overlay.
     * Will be saved without pushing the save button.
     */
    useEffect(() => {
        if (overlayContent === "lieferdetails") {
            storage.set("gas:gtag:current-value", orderComment);
        }
    }, [orderComment, overlayContent]);

    /**
     * Handle the current step index.
     */
    useEffect(() => {
        // Init the checkout, if not exists.
        if (session.get("gas:checkout") == null) {
            calculate(zipcode, consumption).then(() => initCheckout());
        }

        const newIndex = checkoutSteps.findIndex(
            (step) => step.url === checkoutStep
        );

        // If we leave the delivery address step do something with it
        if (checkoutSteps[previousStepIndex]) {
            const previousUrl = checkoutSteps[previousStepIndex].url;
            if (previousUrl === "produkt") {
                storeProductChoiceToStorage();
                // Store to server
                // storeProductToServer();
            }
            if (previousUrl === "lieferadresse") {
                if (stepButtonAction === "next") {
                    storeDeliveryAddressToStorage();
                    storeBillingAddressToStorage();
                    storeDeliveryAddressToServer();
                }
            } else if (previousUrl === "zahlart") {
                if (stepButtonAction === "next") {
                    storePaymentDetailsToServer();
                    storePaymentDataToStorage();
                    storeIbanToStorage();
                    // Store to server
                    // storePaymentTypeToServer();TODO
                }
            } else if (previousUrl === "lieferdetails") {
                if (stepButtonAction === "next") {
                    storeOrderCommentToStorage();
                    storeDeliveryDetailsToStorage();
                    storeDeliveryDetailsToServer();
                    // storePaymentDetailsToServer();
                    // storePaymentDataToStorage(); TODO

                    // Store to server
                } else {
                    setOrderCommentStorageToState();
                    // storePaymentDetailsToServer();
                    storePaymentDataToStorage();
                }
            } else if (previousUrl === "uebersicht") {
                if (stepButtonAction === "next") {
                    if (session.getJson("gas:order") === null) {
                        storeNewsletterToServer();
                        sendOrderToServer();
                    }
                }
            }
        }

        setCurrentStepIndex(newIndex);
        setStepButtonAction(null);
    }, [checkoutStep]);

    /**
     * When the details changed (state) the price should be updated.
     */
    useEffect(() => {
        if (detailsChanged > 0) {
            storeDeliveryDetailsToServer(true);
        }
    }, [detailsChanged]);

    /**
     * Sync product choice with local storage.
     */
    useEffect(() => {
        if (result !== null) {
            // storage.set("gas:product-choice", productChoice);
            syncProductInfos();
        }
    }, [productChoice]);

    /**
     * Sync the product infos into the local storage.
     */
    const syncProductInfos = () => {
        if (result && result.products.length > 0) {
            const chosenProduct = getProductWithPrices();
            storage.set("gas:tarif-id", chosenProduct.tarifID);
            storage.set("gas:tarif-code", chosenProduct.tarifCode);
            storage.set("gas:tarif-name", chosenProduct.tarifName);
            storage.set(
                "gas:total-price",
                chosenProduct.gesamtpreisOhneBoniBrutto
            );
            storage.set(
                "gas:tarif-name-original",
                chosenProduct.tarifNameOrigin
            );
            storage.set("gas:base-price", chosenProduct.grundpreisBrutto);
            setChosenTarifName(chosenProduct.tarifName);
        }
    };

    /**
     * Go to the next step and adjust the router.
     */
    const nextStep = () => {
        if (currentStepIndex + 1 < checkoutSteps.length) {
            const currentStep = checkoutSteps[currentStepIndex].url;
            const nextStep = baseUrl + checkoutSteps[currentStepIndex + 1].url;
            if (currentStep === "lieferadresse") {
                if (!deliveryAddressStepIsValid()) {
                    return;
                }
            } else if (currentStep === "zahlart") {
                if (
                    paymentData.payment_payment_type === 1 &&
                    !paymentStepIsValid()
                ) {
                    return;
                }
                if (!paymentInvoiceIsValid()) {
                    return;
                }
            } else if (currentStep === "lieferdetails") {
                if (!deliveryDetailsIsValid()) {
                    return;
                }
            } else if (currentStep === "uebersicht") {
                if (!agbChecked) {
                    addFormError("agb");
                    return;
                }
            }
            if (formErrors.length === 0) {
                navigate(nextStep);
            }
        }
    };

    /**
     * Go to the previous step and adjust the router.
     */
    const previousStep = () => {
        setFormErrors([]);
        navigate(baseUrl + checkoutSteps[currentStepIndex - 1].url);
    };

    /**
     * When the save button in the overlay is pressed.
     */
    const overlaySave = () => {
        if (overlayContent === "produkt") {
            storeProductChoiceToStorage();
            updateConsumptionProduct();

            // Store to server
            // storeProductToServer();
        } else if (overlayContent === "zahlart") {
            if (
                paymentData.payment_payment_type === 1 &&
                !paymentStepIsValid()
            ) {
                return false;
            }
            if (
                paymentData.payment_different_account_holder === true &&
                !paymentInvoiceIsValid()
            ) {
                return false;
            }
            storePaymentDataToStorage();
            // Store to server
            storePaymentDetailsToServer();
            if (paymentData.payment_payment_type === 1) {
                storeIbanToStorage();
            }
        } else if (overlayContent === "lieferdetails") {
            if (!deliveryDetailsIsValid()) {
                return false;
            }
            // Store to server
            storeDeliveryDetailsToServer();
        } else if (overlayContent === "lieferadresse") {
            if (!deliveryAddressStepIsValid()) {
                return false;
            }
            storeDeliveryAddressToStorage();
            // Store to server
            storeDeliveryAddressToServer();
        } else if (overlayContent === "consumption") {
            if (!consumptionIsValid()) {
                return false;
            }
            storeConsumptionToStorage();

            fetchProducts(zipcode, consumption, "null", place.value)
                .then((data) => {
                    const datajson = JSON.parse(data);
                    setResult(datajson);
                    setLoading(false);
                    updateConsumptionProduct();
                })
                .catch(() => {
                    setResult(null);
                    setLoading(false);
                });
            // Store to server
            //storeConsumptionToServer();
        }
        setOverlayContent(null);
        return true;
    };

    /**
     * When the back button in the overlay is pressed.
     */
    const overlayBack = () => {
        if (overlayContent === "zahlart") {
            setPaymentDataStorageToState();
            paymentTypeHasChanged();
            setIbanStorageToState();
        }
        if (overlayContent === "lieferdetails") {
            setDeliveryDetailsStorageToState();
            detailsHasChanged();
        }
        if (overlayContent === "lieferadresse") {
            setDeliveryAddressStorageToState();
        }
        if (overlayContent === "consumption") {
            setConsumptionStorageToState();
        }

        if (overlayContent === "produkt") {
            setProductChoiceStorageToState();
        }

        setOverlayContent(null);
    };

    /**
     * Check if the button for the previous step should be visible.
     *   if ( currentStepIndex === 1 && currentStepIndex === 3)
     */
    const previousStepVisible = () => {
        if (
            currentStepIndex === 0 ||
            currentStepIndex === 3 ||
            currentStepIndex === 4
        ) {
            return false;
        }
        return true;
    };

    /**
     * Check if the delivery address step is valid.
     */
    const deliveryAddressStepIsValid = () => {
        let errors = [...formErrors];

        // Check if the fields are filled for delivery address
        if (deliveryAddressData.firstname === "") {
            errors.push("firstname");
        }
        if (deliveryAddressData.lastname === "") {
            errors.push("lastname");
        }
        if (deliveryAddressData.birthdate === "") {
            errors.push("birthdate");
        }
        if (deliveryAddressData.phone === "") {
            errors.push("phone");
        }
        if (deliveryAddressData.email === "") {
            errors.push("email");
        }

        // Check if the fields are filled for invoice address
        if (deliveryEqualBilling) {
            if (billingAddressData.billingfirstname === "") {
                errors.push("billingfirstname");
            }
            if (billingAddressData.billinglastname === "") {
                errors.push("billinglastname");
            }
            if (billingAddressData.billingstreet === "") {
                errors.push("billingstreet");
            }
            if (billingAddressData.billingstreet === "") {
                errors.push("billingemail");
            }
            if (billingAddressData.billingstreet === "") {
                errors.push("billingphone");
            }
            if (billingAddressData.billingzipcode === "") {
                errors.push("billingzipcode");
            }
            if (billingAddressData.billingcity === "") {
                errors.push("billingcity");
            }
        } else {
            errors = errors.filter((error) => !error.startsWith("billing"));
        }

        setFormErrors(errors);

        if (errors.length > 0) {
            return false;
        }

        return true;
    };

    /**
     * Check if Payment Step is valid
     */

    const paymentStepIsValid = () => {
        let errors = [...formErrors];

        if (paymentData.payment_account_holder === "") {
            errors.push("payment_account_holder");
        }
        if (paymentData.payment_bank_name === "") {
            errors.push("payment_bank_name");
        }

        if (iban === "") {
            errors.push("iban");
        }
        if (iban.length > 0 && iban.indexOf("*") >= 0) {
            errors.push("iban");
        }
        if (!paymentData.payment_sepa_direct_granted) {
            errors.push("payment_sepa_direct_granted");
        }
        setFormErrors(errors);

        if (errors.length > 0) {
            return false;
        }
        return true;
    };

    /**
     * if contact and delivery address for different user details is valid
     */

    const paymentInvoiceIsValid = () => {
        let errors = [...formErrors];
        if (paymentData.payment_different_account_holder === true) {
            if (paymentData.payment_firstname === "") {
                errors.push("payment_firstname");
            }
            if (paymentData.payment_lastname === "") {
                errors.push("payment_lastname");
            }

            if (paymentData.payment_email === "") {
                errors.push("payment_email");
            }
            if (paymentData.payment_phone === "") {
                errors.push("payment_phone");
            }
            if (paymentData.payment_street === "") {
                errors.push("payment_street");
            }
            if (paymentData.payment_zipcode === "") {
                errors.push("payment_zipcode");
            }
            if (paymentData.payment_city === "") {
                errors.push("payment_city");
            }
        } else {
            errors = errors.filter((error) => !error.startsWith("payment"));
        }

        setFormErrors(errors);

        if (errors.length > 0) {
            return false;
        }

        return true;
    };

    /**
     * Validte delivery details
     */

    const deliveryDetailsIsValid = () => {
        let errors = [...formErrors];
        errors = errors.filter((error) => !error.startsWith("deliveryDetails"));
        if (deliveryDetails.delivery_movement_type === "Umzug/Einzug") {
            if (deliveryDetails.delivery_change_date === "") {
                errors.push("delivery_change_date");
            }
        }

        if (deliveryDetails.delivery_movement_type === "Lieferantenwechsel") {
            if (
                deliveryDetails.delivery_change_date_immediately ===
                    "frühestens" &&
                deliveryDetails.delivery_supplier_terminated_date === ""
            ) {
                errors.push("delivery_supplier_terminated_date");
            }
            if (deliveryDetails.delivery_previous_supplier === "") {
                errors.push("delivery_previous_supplier");
            }
        }
        if (deliveryDetails.delivery_counter_number === "") {
            errors.push("delivery_counter_number");
        }
        if (
            deliveryDetails.delivery_supplier_terminated === "JaTS" &&
            deliveryDetails.delivery_supplier_terminated_date === ""
        ) {
            errors.push("delivery_supplier_terminated_date");
        }

        setFormErrors(errors);

        if (errors.length > 0) {
            return false;
        }

        return true;
    };

    /**
     * Consumption is valid
     */

    const consumptionIsValid = () => {
        let errors = [...formErrors];
        if (consumption === "") {
            errors.push("consumption");
        }
        setFormErrors(errors);

        if (errors.length > 0) {
            return false;
        }

        return true;
    };

    /**
     * Fetch the prices from the api and store them into the result.
     */
    const calculate = async (localZipcode, localConsumption) => {
        if (!result && !loading) {
            // do the calculation
            setLoading(true);
            setResult(null);
            fetchProducts(localZipcode, localConsumption)
                .then((data) => {
                    const datajson = JSON.parse(data);
                    setResult(datajson);
                    setLoading(false);
                })
                .catch(() => {
                    setResult(null);
                    setLoading(false);
                });
        } else {
            // Still loading, do nothing
        }
    };

    /**
     * Indicator for details has changed.
     */
    const detailsHasChanged = () => {
        setDetailsChanged(detailsChanged + 1);
    };

    /**
     * Indicator for payment type has changed.
     */
    const paymentTypeHasChanged = () => {
        setPaymentTypeChanged(paymentTypeChanged + 1);
    };

    /**
     * ###################################################################
     *   Functions to store the values from state to local storage.
     * ###################################################################
     */

    /**
     * Stores the consumption the storage.
     */
    const storeConsumptionToStorage = () => {
        storage.set("gas:consumption", consumption);
    };

    /**
     * Stores the delivery address in the storage.
     */
    const storeDeliveryAddressToStorage = () => {
        session.setJson("gas:delivery-address", deliveryAddressData);
    };

    /**
     * Stores the billing address in the storage.
     */
    const storeBillingAddressToStorage = () => {
        storage.setJson("gas:billing-address", billingAddressData);
    };
    /**
     * Stores Delivery Details to the storage.
     */
    const storeDeliveryDetailsToStorage = () => {
        session.setJson("gas:delivery-details", deliveryDetails);
    };

    /**
     * Store the payment type in the storage.
     */
    const storeOrderCommentToStorage = () => {
        storage.set("gas:order-comment", orderComment);
    };

    /**
     *  Store Product Choice in storage
     */
    const storeProductChoiceToStorage = () => {
        storage.set("gas:product-choice", productChoice);
    };
    /*
     * Store paymentdata to storage
     **/
    const storePaymentDataToStorage = () => {
        storage.setJson("gas:payment-data", paymentData);
    };

    /**
     * store last 4 char Iban to storage
     */
    const storeIbanToStorage = () => {
        if (iban.length === 0) {
            return;
        }
        const tmpIban = iban;
        const fourLastDigit = tmpIban.replace(" ", "").slice(-4);
        const alternateIban = "******************" + fourLastDigit;
        storage.set("gas:iban", alternateIban);
        setIban(alternateIban);
    };

    const updatePaymentDataJson = (key, value) => {
        const copypaymentdata = JSON.parse(JSON.stringify(paymentData));
        copypaymentdata[key] = value;
        setPaymentData(copypaymentdata);
    };

    /**
     * ###################################################################
     *   Functions to store the values from local storage to the state.
     * ###################################################################
     */

    /**
     * Sets the consumption from storage to state.
     */
    const setConsumptionStorageToState = () => {
        setConsumption(storage.get("gas:consumption"));
    };

    /**
     * Sets the delivery address from storage to the state.
     */
    const setDeliveryAddressStorageToState = () => {
        setDeliveryAddressData(session.getJson("gas:delivery-address"));
    };

    const setPaymentDataStorageToState = () => {
        setPaymentData(storage.getJson("gas:payment-data"));
    };

    /**
     * Set Iban storage to state
     */

    const setIbanStorageToState = () => {
        if (storage.get("gas:iban") != null) {
            setIban(storage.get("gas:iban"));
        }
    };

    /**
     * Set the payment type from storage to the state.
     */
    const setOrderCommentStorageToState = () => {
        setOrderComment(storage.get("gas:order-comment", ""));
    };

    /**
     * set Product choice to state
     */
    const setProductChoiceStorageToState = () => {
        setProductChoice(storage.get("gas:product-choice"));
    };
    /**
     * set Product choice to state
     */
    const setDeliveryDetailsStorageToState = () => {
        setDeliveryDetails(session.getJson("gas:delivery-details"));
    };

    /**
     * Returns the current payment type.
     */
    const getCurrentPaymentType = () => {
        return gasPaymentTypes.find(
            (item) => item.id == paymentData.payment_payment_type
        );
    };

    /**
     * Returns the current prices.
     */
    const getPrices = () => {
        if (!checkoutData) {
            return null;
        }

        return {
            monthlyCharge: checkoutData.monthly_charge,
            unitPrice: checkoutData.unit_price,
            priceTotalGross: checkoutData.price_total_gross,
            consumption: checkoutData.consumption,
            tarifNameOriginal: storage.get("gas:tarif-name-original"),
            grundPrice: checkoutData.unit_price,
        };
    };

    /**
     * ###################################################################
     *   Form validation.
     * ###################################################################
     */
    /**
     * Add an error to the errors.
     *
     * @param {*} key
     * @param {*} value
     */
    const addFormError = (value) => {
        setFormErrors([...formErrors, value]);
    };

    /**
     * Remove an element from the errors.
     *
     * @param {*} key
     */
    const clearFormError = (key) => {
        setFormErrors(formErrors.filter((item) => item !== key));
    };

    const clearFormErrorWithStartField = (partOfField) => {
        setFormErrors(
            formErrors.filter((error) => !error.startsWith(partOfField))
        );
    };
    /**
     * Check if the given key is present in the errors.
     *
     * @param {*} key
     */
    const hasFormErrorAt = (key) => {
        return formErrors.filter((item) => item === key).length > 0;
    };

    /**
     * ###################################################################
     *   Functions which communicate with the server.
     * ###################################################################
     */

    /**
     * Init the checkout process.
     */
    const initCheckout = async () => {
        // const tarifId = storage.get("gas:tarif-id");
        const tarifCode = storage.get("gas:tarif-code");

        const response = await apiFetch(
            `/gas/checkout/init/${zipcode}/${consumption}/${tarifCode}/${verbandsnummer}/${place.value}`
        );
        const data = await response.json();
        session.setJson("gas:checkout", data);
        setCheckoutData(data);
        setDeliveryAddressData({
            ...deliveryAddressData,
            ...{ zipcode: zipcode },
        });

        return data;
    };
    /**
     * Update Product and Consumption.
     */
    const updateConsumptionProduct = async () => {
        // const tarifId = storage.get("gas:tarif-id");
        const tarifCode = storage.get("gas:tarif-code");

        const response = await apiFetch(`/gas/checkout/productConsumption`, {
            method: "post",
            headers: {
                "Content-Type": "application/x-www-form-urlencoded",
            },
            body: JSON.stringify({
                hash: checkoutData.user_hash,
                zipcode: zipcode,
                consumption: consumption,
                tarifCode: tarifCode,
                verbandsnummer: verbandsnummer,
                ort: place.value,
            }),
        });
        const data = await response.json();
        session.setJson("gas:checkout", data);
        setCheckoutData(data);

        return data;
    };

    /**
     * Store the delivery address data to the server.
     */
    const storeDeliveryAddressToServer = () => {
        apiFetch("/gas/checkout/deliveryAddress", {
            method: "post",
            headers: { "Content-Type": "application/x-www-form-urlencoded" },
            body: JSON.stringify({
                hash: checkoutData.user_hash,
                deliveryAddressData: { ...deliveryAddressData },
                street: street.value,
                housenumber: houseNum,
                zipcode: zipcode,
                city: place.value,
                deliveryEqualBilling: deliveryEqualBilling,
                billingAddressData: { ...billingAddressData },
            }),
        });
    };

    /**
     * Store the delivery details to the server.
     */
    const storeDeliveryDetailsToServer = () => {
        apiFetch("/gas/checkout/deliveryDetails", {
            method: "post",
            headers: { "Content-Type": "application/x-www-form-urlencoded" },
            body: JSON.stringify({
                hash: checkoutData.user_hash,
                deliveryDetails: { ...deliveryDetails },
            }),
        });
    };

    /**
     * Store the Newsletter to the server.
     */
    const storeNewsletterToServer = () => {
        apiFetch("/gas/checkout/acceptnewsletter", {
            method: "post",
            headers: { "Content-Type": "application/x-www-form-urlencoded" },
            body: JSON.stringify({
                hash: checkoutData.user_hash,
                receive_newsletter: receiveNewsletter,
            }),
        });
    };

    /**
     * Store the payment details to the server.
     */
    const storePaymentDetailsToServer = () => {
        apiFetch("/gas/checkout/paymentDetails", {
            method: "post",
            headers: { "Content-Type": "application/x-www-form-urlencoded" },
            body: JSON.stringify({
                hash: checkoutData.user_hash,
                paymentData: { ...paymentData },
                bic: null,
                iban: iban,
            }),
        });
    };

    /**
     * Creates an order from the checkout
     */
    const sendOrderToServer = () => {
        setLoading(true);
        apiOrderCheckoutFetch("/gas/checkout/finish", {
            method: "post",
            headers: { "Content-Type": "application/x-www-form-urlencoded" },
            body: JSON.stringify({
                hash: checkoutData.user_hash,
                receive_newsletter: receiveNewsletter,
            }),
        })
            .then((response) => response.json())
            .then((data) => {
                session.setJson("gas:order", data);
                pushToAnalytics("order", {
                    orderNumber: data.order_number,
                });
                setOrderData(data);
                setLoading(false);
            });
        // clearCheckout()
    };

    /**
     * Returns the result object for the selected product.
     */
    const getProductWithPrices = () => {
        return result.products[productChoice];
    };

    const data = {
        steps,
        checkoutSteps,
        currentStepIndex,
        deliveryAddressData,
        billingAddressData,
        deliveryDetails,
        deliveryEqualBilling,
        gasPaymentTypes,
        price,
        zipcode,
        consumption,
        editMode,
        orderComment,
        result,
        paymentData,
        formErrors,
        iban,
        agbChecked,
        productChoice,
        chosenTarifName,
        overlayContent,
        checkoutData,
        receiveNewsletter,
        orderData,
        loading,
        place,
        street,
        houseNum,
    };

    const actions = {
        setCurrentStepIndex,
        setDeliveryAddressData,
        setDeliveryDetails,
        setBillingAddressData,
        setDeliveryEqualBilling,
        nextStep,
        addFormError,
        hasFormErrorAt,
        clearFormError,
        setFormErrors,
        setConsumption,
        previousStep,
        setPrice,
        setEditMode,
        setStepButtonAction,
        getCurrentPaymentType,
        setOrderComment,
        setPaymentData,
        setIban,
        setAgbChecked,
        previousStepVisible,
        setOverlayContent,
        updatePaymentDataJson,
        overlayBack,
        overlaySave,
        getPrices,
        detailsHasChanged,
        setProductChoice,
        setChosenTarifName,
        setReceiveNewsletter,
        paymentTypeHasChanged,
        getProductWithPrices,
        clearFormErrorWithStartField,
    };

    return (
        <DataContext.Provider
            value={{
                data,
                actions,
            }}
        >
            {children}
        </DataContext.Provider>
    );
};

export default GasCheckoutDataProvider;
